import { useState, useCallback } from 'react';
import { realtimeAPI } from '../utils/api';

/**
 * Hook for managing real-time Quran recitation tracking
 * Handles word-by-word comparison and error detection
 */
export function useRealtimeTracking() {
  const [sessionId, setSessionId] = useState(null);
  const [isTracking, setIsTracking] = useState(false);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [errors, setErrors] = useState([]);
  const [lastResult, setLastResult] = useState(null);

  const startTracking = useCallback(async (surahNumber, ayahNumber) => {
    try {
      const newSessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      
      const response = await realtimeAPI.startTracking({
        session_id: newSessionId,
        surah_number: surahNumber,
        ayah_number: ayahNumber
      });

      setSessionId(newSessionId);
      setIsTracking(true);
      setCurrentWordIndex(0);
      setErrors([]);
      setLastResult(null);

      return {
        success: true,
        sessionId: newSessionId,
        totalWords: response.data.total_words,
        expectedText: response.data.expected_text
      };
    } catch (error) {
      console.error('Error starting tracking:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to start tracking'
      };
    }
  }, []);

  const processWord = useCallback(async (spokenWord) => {
    if (!sessionId || !isTracking) {
      return { success: false, error: 'No active tracking session' };
    }

    try {
      const response = await realtimeAPI.processWord({
        session_id: sessionId,
        spoken_word: spokenWord
      });

      const result = response.data;
      setLastResult(result);
      setCurrentWordIndex(result.position);

      if (!result.is_correct) {
        setErrors(prev => [...prev, {
          position: result.position,
          expected: result.expected_word,
          spoken: result.spoken_word,
          errorCount: result.error_count
        }]);
      }

      return {
        success: true,
        ...result
      };
    } catch (error) {
      console.error('Error processing word:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to process word'
      };
    }
  }, [sessionId, isTracking]);

  const getSummary = useCallback(async () => {
    if (!sessionId) {
      return null;
    }

    try {
      const response = await realtimeAPI.getSessionSummary(sessionId);
      return response.data;
    } catch (error) {
      console.error('Error getting summary:', error);
      return null;
    }
  }, [sessionId]);

  const reset = useCallback(async () => {
    if (!sessionId) {
      return;
    }

    try {
      await realtimeAPI.resetSession(sessionId);
      setCurrentWordIndex(0);
      setErrors([]);
      setLastResult(null);
    } catch (error) {
      console.error('Error resetting session:', error);
    }
  }, [sessionId]);

  const endTracking = useCallback(async () => {
    if (!sessionId) {
      return null;
    }

    try {
      const response = await realtimeAPI.endSession(sessionId);
      setIsTracking(false);
      setSessionId(null);
      return response.data.summary;
    } catch (error) {
      console.error('Error ending session:', error);
      setIsTracking(false);
      setSessionId(null);
      return null;
    }
  }, [sessionId]);

  return {
    sessionId,
    isTracking,
    currentWordIndex,
    errors,
    lastResult,
    startTracking,
    processWord,
    getSummary,
    reset,
    endTracking
  };
}
